import {
  patchState,
  signalStore,
  withMethods,
  withState,
  withComputed,
} from '@ngrx/signals';
import { inject, computed, Injector } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  AccountService,
  AuthService,
  AuthStore,
  IAuthAccessToken,
  IAuthLoginPayload,
  IChangePaswordRequest,
  TGroup,
} from '@account/data-access';
import { Encrypt, getCyclicColor } from '@shared/utils';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  filter,
  firstValueFrom,
  fromEvent,
  interval,
  merge,
  Observable,
  pipe,
  Subscription,
  switchMap,
} from 'rxjs';
import { Subject } from 'rxjs';
import { tap, take } from 'rxjs/operators';
import { AlertStore } from '@alert/data-access';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { tapResponse } from '@ngrx/operators';
import { IPersonDataAsync } from './entities/personDataAsync.interface';
import { PersonService } from '@domains/person/register/data';
import { adminTenants } from '@application/config';
import {
  TemporaryAuthModalConfig,
  TemporaryAuthModalService,
} from '@shared/components/ui/temporary-auth-modal';
import { SystemModulesStore } from './system-modules.store';
import { ErrorStore } from '@application/errors/data-access';
import { IUser, IUserRole, UserProfile } from './entities/self-user.types';
import { SelfUserApiService } from './infra/self-user-api.service';
import { LocalStorageFacade } from '@application/localstorage';
// eslint-disable-next-line @nx/enforce-module-boundaries -- userSettings/uiClientSettings types shared with shortcuts; consider moving to person/user or shared types lib to break cycle
import { IUserSettings } from '@shared/components/shortcuts/data-access';
import { DateTime } from 'luxon';

import {
  IUiClientSettings,
  applyUiClientSettingsPatches,
  parseUiClientSettings,
  mergeUiClientSettings,
  DefaultUiClientSettings,
  storageFormatToSearchOptions,
  storageFormatToPersonActions,
} from '@shared/components/shortcuts/data-access';

export interface ISelfUserStore {
  personDataAsync: IPersonDataAsync;
  userData: IUser;
  userRole: IUserRole;
  allPersonsByUser: IUserRole[];
  userProfile: UserProfile;
  impersonatedUserProfile: UserProfile;
  temporaryPermissionsExpiresAt: number | null;
  userSettings: IUserSettings | null;
  uiClientSettings: IUiClientSettings;
  /** True when uiClientSettings were modified locally and not yet persisted */
  uiClientSettingsDirty: boolean;
  /** Local patches waiting for the user settings record to load or persist. */
  pendingUiClientSettingsPatches: Partial<IUiClientSettings>[];
  groupedUnits: TGroup;
}

/** Emits when uiClientSettings should be persisted. Subscribed by ShortcutsStore. */
export const uiClientSettingsPersistenceSubject = new Subject<void>();

export const initialSelfUserStore: ISelfUserStore = {
  personDataAsync: null,
  userData: null,
  userRole: null,
  allPersonsByUser: null,
  userProfile: null,
  impersonatedUserProfile: null,
  temporaryPermissionsExpiresAt: null,
  userSettings: null,
  uiClientSettings: DefaultUiClientSettings,
  uiClientSettingsDirty: false,
  pendingUiClientSettingsPatches: [],
  groupedUnits: null,
};

const TEMPORARY_AUTH_TRANSLATION_PATH = 'TEMPORARY_AUTH_MODAL';

/**
 * As permissões do usuário logado (`userProfile.ClientPermissions`) chegam uma vez, no
 * boot do app. Sem reler, uma função editada em Configurações › Usuários › Funções não
 * chegava à sessão aberta: menu, atalhos e `ensurePermissions` continuavam decidindo
 * pelas permissões antigas até um F5/novo login — numa recepção que deixa o app aberto
 * o dia todo, isso é "nunca". O backend já serve o perfil fresco (lê a Função do banco
 * e limpa o `auth-users` ao salvar a Função); quem segurava o valor velho era a store.
 */
/** Releitura periódica enquanto a aba está visível. */
const SELF_PROFILE_REFRESH_INTERVAL_MS = 5 * 60_000;
/** Ao voltar para a aba, só relê se a última leitura passou disto. */
const SELF_PROFILE_REFRESH_ON_FOCUS_MAX_AGE_MS = 60_000;
/**
 * Antes de negar uma permissão, relê o perfil se a última leitura passou disto — a
 * permissão pode ter sido concedida depois do boot. Curto o bastante para valer logo,
 * longo o bastante para várias telas negadas de uma vez não virarem várias requisições.
 */
const SELF_PROFILE_RECHECK_MAX_AGE_MS = 30_000;

/**
 * Pure check: does `profile` hold every permission in `permissions` (or is owner).
 * With an empty `permissions` list this returns `true` for any profile — callers
 * that only need a valid privileged login (e.g. the 403 elevation flow) skip the
 * check entirely instead of relying on this.
 */
const profileHasPermissions = (
  profile: UserProfile,
  permissions: number[]
): boolean => {
  if (!profile) {
    return false;
  }
  if (profile.IsOwner) {
    return true;
  }
  const clientPermissions = profile.ClientPermissions;
  if (!clientPermissions?.length) {
    return false;
  }
  const allowed = new Set(
    clientPermissions
      .filter(({ Checked }) => Checked)
      .map(({ Permission }) => Permission)
  );
  return permissions.every((permission) => allowed.has(permission));
};

/** Grace period (ms) after a local ElasticSearch change during which server settings are not applied. */
export const ELASTIC_SEARCH_LOCAL_CHANGE_GRACE_PERIOD_MS = 2000;

export const SelfUserStore = signalStore(
  { providedIn: 'root' },
  withState(initialSelfUserStore),
  withComputed((state, systemModulesStore = inject(SystemModulesStore), authStore = inject(AuthStore)) => ({
    licensedModuleNames: computed(() => systemModulesStore.moduleNames()),
    authorization: computed(() => authStore.getAuthData()),
  })),
  withMethods(
    (
      state,
      injector = inject(Injector),
      authService = inject(AuthService),
      authStore = inject(AuthStore),
      accountService = inject(AccountService),
      alertStore = inject(AlertStore),
      errorStore = inject(ErrorStore),
      crypt = inject(Encrypt),
      selfUserApiService = inject(SelfUserApiService),
      temporaryAuthModalService = inject(TemporaryAuthModalService),
      localStorageFacade = inject(LocalStorageFacade),
      doc = inject(DOCUMENT)
    ) => {
      const getAccountService = (): AccountService =>
        injector.get(AccountService);
      const getPersonService = (): PersonService => injector.get(PersonService);
      let temporaryPermissionTimeout: ReturnType<typeof setTimeout> | null =
        null;

      let lastSelfProfileFetchAt = 0;
      let selfProfileRefreshInFlight: Promise<UserProfile> | null = null;
      let selfProfileAutoRefresh: Subscription | null = null;

      /**
       * Relê o perfil do usuário logado em silêncio (sem mexer no req-state do
       * `loadSelfUserProfile`, que os guards observam) e devolve o mais recente.
       *
       * Não relê durante uma sessão de autenticação temporária: nela o token é o do
       * outro usuário, e o `GetByAuthPersonAsync` devolveria o perfil dele no lugar do
       * nosso. Pelo mesmo motivo a resposta é descartada se a sessão temporária abriu
       * enquanto a requisição estava no ar.
       */
      const refreshSelfProfile = (maxAgeMs: number): Promise<UserProfile> => {
        const current = state.userProfile();
        if (!current || authStore.isTemporaryAuthActive()) {
          return Promise.resolve(current);
        }
        if (DateTime.now().toMillis() - lastSelfProfileFetchAt < maxAgeMs) {
          return Promise.resolve(current);
        }
        if (selfProfileRefreshInFlight) {
          return selfProfileRefreshInFlight;
        }

        lastSelfProfileFetchAt = DateTime.now().toMillis();
        selfProfileRefreshInFlight = firstValueFrom(
          selfUserApiService.getSelfUserProfile()
        )
          .then((profile) => {
            if (profile && !authStore.isTemporaryAuthActive()) {
              patchState(state, { userProfile: profile });
            }
            return state.userProfile();
          })
          .catch(() => state.userProfile())
          .finally(() => {
            selfProfileRefreshInFlight = null;
          });

        return selfProfileRefreshInFlight;
      };

      /**
       * Liga a releitura automática uma vez por sessão do app: ao voltar para a aba e a
       * cada {@link SELF_PROFILE_REFRESH_INTERVAL_MS} com ela visível. Só arranca depois
       * do primeiro perfil carregado, então logout/login não deixam nada para trás — o
       * `refreshSelfProfile` não faz nada sem perfil na store.
       */
      const startSelfProfileAutoRefresh = () => {
        if (selfProfileAutoRefresh) return;

        const isVisible = () => doc.visibilityState !== 'hidden';

        selfProfileAutoRefresh = merge(
          fromEvent(doc, 'visibilitychange').pipe(
            filter(isVisible),
            switchMap(() =>
              refreshSelfProfile(SELF_PROFILE_REFRESH_ON_FOCUS_MAX_AGE_MS)
            )
          ),
          interval(SELF_PROFILE_REFRESH_INTERVAL_MS).pipe(
            filter(isVisible),
            switchMap(() =>
              refreshSelfProfile(SELF_PROFILE_REFRESH_INTERVAL_MS / 2)
            )
          )
        ).subscribe();
      };

      const clearTemporaryPermissionState = () => {
        if (temporaryPermissionTimeout) {
          clearTimeout(temporaryPermissionTimeout);
          temporaryPermissionTimeout = null;
        }

        patchState(state, {
          impersonatedUserProfile: null,
          temporaryPermissionsExpiresAt: null,
        });
      };

      /**
       * Builds the `authProvider` used by the temporary-auth modal: logs the
       * privileged user in (handling cross-tenant login via
       * `getAllPersonsByAnotherUser` + `changePersonAccount`), starts the
       * temporary session and loads the impersonated profile into state.
       *
       * When `verifyPermissions` is true it also asserts the impersonated user
       * actually holds `permissions` before signalling success (used by
       * `ensurePermissions`). When false, any successful privileged login is
       * accepted — the caller relies on the server to re-check on the retried
       * request (used by `requestTemporaryElevation` for HTTP 403 replay, where
       * the 403 body does not tell us which permission was missing).
       */
      const buildTemporaryAuthProvider =
        (permissions: number[], duration: number, verifyPermissions: boolean) =>
        (payload: IAuthLoginPayload): Observable<IAuthAccessToken> => {
          return new Observable<IAuthAccessToken>((observer) => {
            const fail = (err: unknown) => {
              observer.error(err);
              observer.complete();
            };

            const verifyAndEmit = async (authData: IAuthAccessToken) => {
              try {
                authStore.beginTemporaryAuthSession(authData, duration);
                const impersonatedProfile = (await firstValueFrom(
                  selfUserApiService.getSelfUserProfile()
                )) as UserProfile;

                if (
                  verifyPermissions &&
                  !profileHasPermissions(impersonatedProfile, permissions)
                ) {
                  authStore.endTemporaryAuthSession();
                  clearTemporaryPermissionState();
                  alertStore.create({
                    type: 'warning',
                    title: `${TEMPORARY_AUTH_TRANSLATION_PATH}.ALERT.INSUFFICIENT_PERMISSIONS_TITLE`,
                    message: `${TEMPORARY_AUTH_TRANSLATION_PATH}.ALERT.INSUFFICIENT_PERMISSIONS_MESSAGE`,
                  });
                  fail(new Error('INSUFFICIENT_PERMISSIONS'));
                  return;
                }

                patchState(state, {
                  impersonatedUserProfile: impersonatedProfile,
                  temporaryPermissionsExpiresAt: DateTime.now().toMillis() + duration,
                });
                observer.next(authData);
                observer.complete();
              } catch (e) {
                authStore.endTemporaryAuthSession();
                clearTemporaryPermissionState();
                fail(e);
              }
            };

            authService
              .login(payload)
              .pipe(take(1))
              .subscribe({
                next: (authData) => {
                  const tenantId = state.personDataAsync()?.TenantId;
                  if (!tenantId) {
                    fail(new Error('TenantId not found'));
                    return;
                  }
                  if (!authData?.Token) {
                    fail(new Error('Token not found'));
                    return;
                  }
                  if (authData?.TenantId === tenantId) {
                    verifyAndEmit(authData);
                    return;
                  }
                  authService
                    .getAllPersonsByAnotherUser(authData.Token)
                    .pipe(take(1))
                    .subscribe({
                      next: (persons) => {
                        const person = persons?.find(
                          (p) => p.TenantId === tenantId
                        );
                        if (!person) {
                          fail(new Error('Person not found'));
                          return;
                        }
                        authService
                          .changePersonAccount(person.PersonId)
                          .pipe(take(1))
                          .subscribe({
                            next: (newAuthData) => verifyAndEmit(newAuthData),
                            error: fail,
                          });
                      },
                      error: fail,
                    });
                },
                error: fail,
              });
          });
        };

      return {
        clearSelfUserStore() {
          clearTemporaryPermissionState();
          patchState(state, {
            ...initialSelfUserStore,
            ...setPropInit(
              'loadSelfUser',
              'loadSelfUserProfile',
              'loadSelfUserRole',
              'changeMyPassword',
              'getPersonDataAsync'
            ),
          });
        },

        /**
         * Sets userSettings and parsed uiClientSettings from API response (e.g. getShortcuts).
         */
        setUserSettingsFromResponse(
          data: IUserSettings | null,
          preservePendingChanges = false
        ): void {
          const pendingPatches = preservePendingChanges
            ? state.pendingUiClientSettingsPatches()
            : [];
          patchState(state, {
            userSettings: data,
            uiClientSettings: applyUiClientSettingsPatches(
              parseUiClientSettings(data?.UiClientSettings),
              pendingPatches
            ),
            uiClientSettingsDirty: pendingPatches.length > 0,
            pendingUiClientSettingsPatches: pendingPatches,
          });
        },

        /**
         * Settles a save the server accepted. `data.UiClientSettings` is what was
         * written — the server's blob with the first `appliedCount` pending
         * patches on top — so only the patches queued after the save started
         * stay pending, applied over it.
         */
        settleUiClientSettingsSave(data: IUserSettings, appliedCount: number): void {
          const remaining = state
            .pendingUiClientSettingsPatches()
            .slice(appliedCount);
          patchState(state, {
            userSettings: data,
            uiClientSettings: applyUiClientSettingsPatches(
              parseUiClientSettings(data.UiClientSettings),
              remaining
            ),
            uiClientSettingsDirty: remaining.length > 0,
            pendingUiClientSettingsPatches: remaining,
          });
        },

        /**
         * Updates the local uiClientSettings state with a partial update.
         * Does not call the API - use persistUiClientSettings for persistence.
         */
        patchUiClientSettings(partial: Partial<IUiClientSettings>): void {
          const currentSettings = state.uiClientSettings();
          const merged = mergeUiClientSettings(currentSettings, partial);
          patchState(state, {
            uiClientSettings: merged,
            uiClientSettingsDirty: true,
            pendingUiClientSettingsPatches: [
              ...state.pendingUiClientSettingsPatches(),
              partial,
            ],
          });
        },

        /**
         * Triggers persistence of the current uiClientSettings to the API.
         * Debounced (100ms) to avoid flooding the API.
         */
        persistUiClientSettings(): void {
          uiClientSettingsPersistenceSubject.next();
        },

        /**
         * Centralized logic: whether to apply server ElasticSearch settings and the merged values.
         * Used by ElasticSearchComponent to sync uiClientSettings.ElasticSearch into local UI state.
         * Returns null when nothing should be applied (no stored data, within grace period, or no changes).
         */
        getElasticSearchStateToApply(
          currentSearchOptions: Array<{
            type: string;
            enabled: boolean;
            tabActive: boolean;
          }>,
          currentPersonActions: Array<{ id: string; enabled: boolean }>,
          lastLocalChangeTimestamp: number,
          hasServerSettingsApplied: boolean,
          defaultSearchOptions: Array<{
            type: string;
            enabled: boolean;
            tabActive: boolean;
          }>,
          defaultPersonActions: Array<{ id: string; enabled: boolean }>
        ): {
          searchOptions: typeof currentSearchOptions;
          personActions: typeof currentPersonActions;
        } | null {
          const uiClientSettings = state.uiClientSettings();
          const elastic = uiClientSettings?.ElasticSearch;
          if (!elastic) {
            return null;
          }
          const {
            SearchOptions: storedSearchOptions,
            PersonActions: storedPersonActions,
          } = elastic;
          if (
            (!storedSearchOptions || storedSearchOptions.length === 0) &&
            (!storedPersonActions || storedPersonActions.length === 0)
          ) {
            return null;
          }
          const timeSinceLastLocalChange =
            DateTime.now().toMillis() - lastLocalChangeTimestamp;
          if (
            hasServerSettingsApplied &&
            timeSinceLastLocalChange <
              ELASTIC_SEARCH_LOCAL_CHANGE_GRACE_PERIOD_MS
          ) {
            return null;
          }
          let searchOptionsResult: typeof currentSearchOptions | null = null;
          let personActionsResult: typeof currentPersonActions | null = null;
          if (storedSearchOptions && storedSearchOptions.length > 0) {
            const merged = storageFormatToSearchOptions(
              storedSearchOptions,
              defaultSearchOptions
            );
            const hasChanges = merged.some(
              (opt, idx) =>
                opt.enabled !== currentSearchOptions[idx]?.enabled ||
                opt.tabActive !== currentSearchOptions[idx]?.tabActive
            );
            if (hasChanges) {
              searchOptionsResult = merged as typeof currentSearchOptions;
            }
          }
          if (storedPersonActions && storedPersonActions.length > 0) {
            const merged = storageFormatToPersonActions(
              storedPersonActions,
              defaultPersonActions
            );
            const hasChanges = merged.some(
              (action, idx) =>
                action.enabled !== currentPersonActions[idx]?.enabled
            );
            if (hasChanges) {
              personActionsResult = merged as typeof currentPersonActions;
            }
          }
          if (searchOptionsResult === null && personActionsResult === null) {
            return null;
          }
          return {
            searchOptions:
              searchOptionsResult ??
              (currentSearchOptions as typeof currentSearchOptions),
            personActions:
              personActionsResult ??
              (currentPersonActions as typeof currentPersonActions),
          };
        },

        isAdminScope: (): boolean => {
          const tenantId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.TenantId;
          const isAdmin = tenantId === adminTenants?.[1].account;
          const isPaguelaAdmin = tenantId === adminTenants?.[5].account;
          const isFacilFitAdmin = tenantId === adminTenants?.[8].account;
          const isToletusAdmin = tenantId === adminTenants?.[9].account;
          const isEdizAdmin = tenantId === adminTenants?.[11].account;
          const isTickouAdmin = tenantId === adminTenants?.[12].account;

          const isAdminScope =
            isAdmin ||
            isPaguelaAdmin ||
            isFacilFitAdmin ||
            isToletusAdmin ||
            isEdizAdmin ||
            isTickouAdmin;
          return isAdminScope;
        },

        isPaguelaAdmin: (): boolean => {
          const tenantId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.TenantId;
          const isPaguelaAdmin =
            tenantId ===
            adminTenants.find((t) => t?.name === 'Paguela')?.account;
          return isPaguelaAdmin;
        },

        isFacilFitAdmin: (): boolean => {
          const tenantId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.TenantId;
          const isFacilFitAdmin =
            tenantId ===
            adminTenants.find((t) => t?.name === 'Facilfit')?.account;
          return isFacilFitAdmin;
        },

        isToletusAdmin: (): boolean => {
          const tenantId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.TenantId;
          return (
            tenantId ===
            adminTenants.find((t) => t?.name === 'Toletus')?.account
          );
        },

        isFacilFitOrActuarAdmin: (): boolean => {
          const tenantId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.TenantId;
          const isFacilFitAdmin =
            tenantId ===
            adminTenants.find((t) => t?.name === 'Facilfit')?.account;
          const isActuarAdmin = tenantId === adminTenants?.[1].account; // Actuar admin
          const isEdizAdmin = tenantId === adminTenants?.[11].account;
          return isFacilFitAdmin || isActuarAdmin || isEdizAdmin;
        },

        isThiago: (): boolean => {
          const personId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.PersonId;
          return personId === '0201cf20-a8fc-43e8-a792-8686e52946fd';
        },

        isTestAccount: (): boolean => {
          const tenantId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.TenantId;
          const isTestAccount = tenantId === adminTenants?.[0].account;
          return isTestAccount;
        },

        isK11Tenant: (): boolean => {
          const tenantId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.TenantId;
          return (
            tenantId === adminTenants.find((t) => t?.name === 'K11')?.account
          );
        },

        getSelfPersonId: (): string => {
          const personId = JSON.parse(
            localStorageFacade.getItem('authorization')
          )?.PersonId;
          return personId;
        },

        getAuthorization: (): IAuthAccessToken => {
          const authorization = localStorageFacade.getItem('authorization');
          return authorization ? JSON.parse(authorization) : null;
        },

        hasClientPermission(permission: number | number[]): boolean {
          const profile =
            state.impersonatedUserProfile() || state.userProfile();
          if (!profile) return false;

          if (profile.IsOwner) {
            return true;
          }

          const clientPermissions = profile.ClientPermissions;
          if (!clientPermissions?.length) {
            return false;
          }

          const permissionsToCheck = Array.isArray(permission)
            ? permission
            : [permission];

          return clientPermissions.some(
            ({ Permission, Checked }) =>
              Checked && permissionsToCheck.includes(Permission)
          );
        },

        /** True if the user has at least one checked ClientPermission (or IsOwner). Used to redirect to access-revoked only when the user has no permissions at all. */
        hasAnyClientPermission(): boolean {
          const profile = state.userProfile();
          if (profile?.IsOwner) {
            return true;
          }
          const clientPermissions = profile?.ClientPermissions;
          if (!clientPermissions?.length) {
            return false;
          }
          return clientPermissions.some(({ Checked }) => Checked);
        },

        hasClientPermissionGroup(permissionGroup: string | string[]): boolean {
          const profile = state.userProfile();

          if (profile?.IsOwner) {
            return true;
          }

          const clientPermissions = profile?.ClientPermissions;

          if (!clientPermissions?.length) {
            return false;
          }

          const groups = Array.isArray(permissionGroup)
            ? permissionGroup
            : [permissionGroup];

          return clientPermissions.some(
            ({ Group, Checked }) => Checked && groups.includes(Group)
          );
        },

        hasAllClientPermissions(permissions: number[]): boolean {
          if (!Array.isArray(permissions) || permissions.length === 0) {
            return false;
          }

          const profile =
            state.impersonatedUserProfile() || state.userProfile();
          if (!profile) return false;

          if (profile.IsOwner) {
            return true;
          }

          const clientPermissions = profile.ClientPermissions;
          if (!clientPermissions?.length) {
            return false;
          }

          const allowedPermissions = new Set(
            clientPermissions
              .filter(({ Checked }) => Checked)
              .map(({ Permission }) => Permission)
          );

          return permissions.every((permission) =>
            allowedPermissions.has(permission)
          );
        },

        ensurePermissions: async (
          permissions: number[],
          config?: Omit<TemporaryAuthModalConfig, 'requiredPermissions'> & {
            interactive?: boolean;
          }
        ): Promise<boolean> => {
          if (!Array.isArray(permissions) || permissions.length === 0) {
            return true;
          }
          const interactive = config?.interactive ?? true;

          const hasPermissionsInProfile = (profile: UserProfile): boolean => {
            if (!profile) {
              return false;
            }

            if (profile.IsOwner) {
              return true;
            }

            const clientPermissions = profile.ClientPermissions;

            if (!clientPermissions?.length) {
              return false;
            }

            const allowed = new Set(
              clientPermissions
                .filter(({ Checked }) => Checked)
                .map(({ Permission }) => Permission)
            );

            return permissions.every((permission) => allowed.has(permission));
          };

          let currentProfile = state.userProfile();
          if (!currentProfile) {
            const loadState = (state as Record<string, () => unknown>)[
              'loadSelfUserProfileSWTState'
            ]?.();
            if (loadState === 'loading') {
              const deadline = DateTime.now().toMillis() + 5000;
              while (!currentProfile && DateTime.now().toMillis() < deadline) {
                await new Promise((r) => setTimeout(r, 50));
                currentProfile = state.userProfile();
                const s = (state as Record<string, () => unknown>)[
                  'loadSelfUserProfileSWTState'
                ]?.();
                if (currentProfile || s === 'loaded') {
                  currentProfile = state.userProfile();
                  break;
                }
              }
            }
            if (!currentProfile) {
              try {
                currentProfile = await firstValueFrom(
                  selfUserApiService.getSelfUserProfile()
                );
                if (currentProfile) {
                  patchState(state, { userProfile: currentProfile });
                }
              } catch {
                // Ignore fallback profile loading errors and continue auth flow.
              }
            }
          }

          if (hasPermissionsInProfile(currentProfile)) {
            return true;
          }
          const basicUserData = state.userData();
          if (basicUserData?.IsOwner) {
            return true;
          }

          const activeImpersonation = state.impersonatedUserProfile();
          const expiration = state.temporaryPermissionsExpiresAt();

          if (expiration && expiration <= DateTime.now().toMillis()) {
            authStore.endTemporaryAuthSession();
            clearTemporaryPermissionState();
          } else if (
            activeImpersonation &&
            expiration &&
            expiration > DateTime.now().toMillis()
          ) {
            if (hasPermissionsInProfile(activeImpersonation)) {
              return true;
            }
          }

          if (hasPermissionsInProfile(state.userProfile())) {
            return true;
          }

          // A permissão pode ter sido concedida à Função depois que o perfil foi lido.
          if (
            hasPermissionsInProfile(
              await refreshSelfProfile(SELF_PROFILE_RECHECK_MAX_AGE_MS)
            )
          ) {
            return true;
          }

          clearTemporaryPermissionState();

          if (!interactive) {
            return false;
          }

          const duration = config?.durationMs ?? 30_000;

          const modalResult = await firstValueFrom(
            temporaryAuthModalService.open({
              requiredPermissions: permissions,
              title: config?.title,
              message: config?.message,
              durationMs: config?.durationMs,
              authProvider: buildTemporaryAuthProvider(
                permissions,
                duration,
                true
              ),
            })
          ).catch(() => null);

          // Permission is verified inside the auth provider; a success result means
          // the impersonated profile + temporary session are already set.
          if (
            !modalResult ||
            modalResult.status !== 'success' ||
            !modalResult.authData
          ) {
            if (state.impersonatedUserProfile()) {
              authStore.endTemporaryAuthSession();
              clearTemporaryPermissionState();
            }
            return false;
          }

          temporaryPermissionTimeout = setTimeout(() => {
            clearTemporaryPermissionState();
            authStore.endTemporaryAuthSession();
          }, duration);

          return true;
        },

        /**
         * Opens the temporary-auth modal to obtain a privileged login WITHOUT a
         * specific permission requirement, then starts a temporary session so the
         * caller can replay a request that the server rejected with HTTP 403.
         *
         * Unlike `ensurePermissions`, this does not know (and cannot derive from
         * the opaque 403 body) which permission was missing, so it accepts any
         * successful privileged login and lets the server re-check on the retry.
         * Returns `true` when the temporary session is active, `false` on cancel
         * or auth failure. Used by the `ForbiddenInterceptor`.
         */
        requestTemporaryElevation: async (
          config?: Pick<TemporaryAuthModalConfig, 'title' | 'message' | 'durationMs'>
        ): Promise<boolean> => {
          const basicUserData = state.userData();
          if (basicUserData?.IsOwner) {
            return false;
          }

          const duration = config?.durationMs ?? 30_000;

          const modalResult = await firstValueFrom(
            temporaryAuthModalService.open({
              requiredPermissions: [],
              title: config?.title,
              message: config?.message,
              durationMs: config?.durationMs,
              authProvider: buildTemporaryAuthProvider([], duration, false),
            })
          ).catch(() => null);

          if (
            !modalResult ||
            modalResult.status !== 'success' ||
            !modalResult.authData
          ) {
            if (state.impersonatedUserProfile()) {
              authStore.endTemporaryAuthSession();
              clearTemporaryPermissionState();
            }
            return false;
          }

          temporaryPermissionTimeout = setTimeout(() => {
            clearTemporaryPermissionState();
            authStore.endTemporaryAuthSession();
          }, duration);

          return true;
        },

        loadSelfUser: rxMethod<void>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('loadSelfUser') })),
            switchMap(() =>
              selfUserApiService.getSelfUser().pipe(
                tapResponse({
                  next: (data: IUser) => {
                    const activeAccount =
                      localStorageFacade.getItemRaw('activeAccount');

                    if (activeAccount) {
                      const activeAccountParsed = JSON.parse(activeAccount);
                      data.backgroundColor =
                        activeAccountParsed?.backgroundColor;
                    } else {
                      data.backgroundColor = 'bg-purple';
                    }

                    patchState(state, {
                      userData: data,
                      ...setPropLoaded('loadSelfUser'),
                    });
                  },
                  error: (error) => {
                    patchState(state, {
                      ...setPropError('loadSelfUser', error),
                    });
                  },
                })
              )
            )
          )
        ),
        /**
         * Relê já o perfil (e as permissões) do usuário logado, sem esperar a releitura
         * automática. Use depois de uma ação que possa ter mudado as permissões dele —
         * ex.: salvar uma Função, que pode ser a do próprio usuário.
         */
        refreshSelfUserProfile(): Promise<UserProfile> {
          return refreshSelfProfile(0);
        },
        loadSelfUserProfile: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('loadSelfUserProfile') })
            ),
            switchMap(() =>
              selfUserApiService.getSelfUserProfile().pipe(
                tapResponse({
                  next: (data: UserProfile) => {
                    lastSelfProfileFetchAt = DateTime.now().toMillis();
                    patchState(state, {
                      userProfile: data,
                      ...setPropLoaded('loadSelfUserProfile'),
                    } as any);
                    startSelfProfileAutoRefresh();
                  },
                  error: (error) => {
                    patchState(state, {
                      ...setPropError('loadSelfUserProfile', error),
                    });
                  },
                })
              )
            )
          )
        ),
        loadSelfUserRole: rxMethod(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('loadSelfUserRole') })
            ),
            switchMap(() =>
              selfUserApiService.getSelfUserRole().pipe(
                tapResponse({
                  next: (data: IUserRole) => {
                    patchState(state, {
                      userRole: data,
                      ...setPropLoaded('loadSelfUserRole'),
                    });
                  },
                  error: (error) => {
                    patchState(state, {
                      ...setPropError('loadSelfUserRole', error),
                    });
                  },
                })
              )
            )
          )
        ),
        changeMyPassword: rxMethod<IChangePaswordRequest>(
          pipe(
            tap((payload: IChangePaswordRequest) => {
              patchState(state, { ...setPropLoading('changeMyPassword') });
              payload.OldPassword = crypt.encryptMd5(
                payload.Email + payload.OldPassword
              );
              payload.NewPassword = crypt.encryptMd5(
                payload.Email + payload.NewPassword
              );
              return payload;
            }),
            switchMap((payload) =>
              getAccountService()
                .changeSelfUserPassword(payload)
                .pipe(
                  tapResponse({
                    next: () => {
                      patchState(state, {
                        ...setPropLoaded('changeMyPassword'),
                      });
                      alertStore.create({
                        title: 'ALERT.TITLE_SUCCESS',
                        type: 'success',
                        message:
                          'PROFILEMENU.ACCOUNT.ALERT.CHANGE_PASSWORD.MESSAGE',
                      });
                    },
                    error: (error: any) => {
                      patchState(state, {
                        ...setPropError('changeMyPassword', error),
                      });
                      errorStore.setHttpError(error);
                    },
                  })
                )
            )
          )
        ),
        getAllPersonByUser: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('getAllPersonsByUser') })
            ),
            switchMap(() =>
              selfUserApiService.getAllPersonsByUser().pipe(
                tapResponse({
                  next: (data) => {
                    data.forEach((item, index) => {
                      const userData = state.userData();
                      item.backgroundColor = `${getCyclicColor(index)}`;
                      if (item.PersonId === state.userData().PersonId) {
                        item.backgroundColor = userData.backgroundColor;
                      }
                    });
                    patchState(state, {
                      allPersonsByUser: data,
                      ...setPropLoaded('getAllPersonsByUser'),
                    });
                  },
                  error: (error) => {
                    patchState(state, {
                      ...setPropError('getAllPersonsByUser', error),
                    });
                  },
                })
              )
            )
          )
        ),

        getPersonDataAsync: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('getPersonDataAsync') })
            ),
            switchMap(() =>
              getPersonService()
                .getUserDataAsync()
                .pipe(
                  tapResponse({
                    next: (personDataAsync) => {
                      patchState(state, {
                        personDataAsync,
                        ...setPropLoaded('getPersonDataAsync'),
                      });
                    },
                    error: (error) => {
                      patchState(state, {
                        ...setPropError('getPersonDataAsync', error),
                      });
                    },
                  })
                )
            )
          )
        ),

        getGroupByMemberTenantId: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(state, {
                ...setPropLoading('getGroupByMemberTenantId'),
              })
            ),
            switchMap((memberTenantId) =>
              accountService.getGroupByMemberTenantId(memberTenantId).pipe(
                tapResponse({
                  next: (groupedUnits) => {
                    patchState(state, {
                      groupedUnits,
                      ...setPropLoaded('getGroupByMemberTenantId'),
                    });
                  },
                  error: (error) => {
                    patchState(state, {
                      ...setPropError('getGroupByMemberTenantId', error),
                    });
                  },
                })
              )
            )
          )
        ),
      };
    }
  ),
  withReqState(
    'loadSelfUser',
    'loadSelfUserProfile',
    'loadSelfUserRole',
    'changeMyPassword',
    'getAllPersonsByUser',
    'getPersonDataAsync',
    'getGroupByMemberTenantId'
  )
);
